import {
  Button,
  Checkbox,
  HashDisplay,
  Input,
  Loading,
  Select,
  Textarea,
  Toggle,
  TrivialDownload,
  TrivialTooltip,
} from '@chiroup/components';
import {
  AllInvoiceStatusOptions,
  BusinessRuleIssueType,
  BusinessRuleList,
  ChiroUpJSON,
  CreateEdiPostDefaults,
  CreateEdiPostType,
  FormErrors,
  Invoice,
  InvoiceDetailType,
  InvoiceStatusEnum,
  InvoiceStatusOptions,
  PayorPriorityIdText,
  PayorPriorityOptions,
  SELECT_OPTION_TYPE,
  STRING_ANY_HASH,
  STRING_BOOLEAN_HASH,
  classNames,
  setPropertyValueByPath,
} from '@chiroup/core';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useContext, useEffect, useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useLocation } from 'react-router-dom';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import businessRulesService from '../../services/businessRules.service';
import patientBillingService from '../../services/patientBilling.service';
import Header from '../layout/Header';
import Tabs from '../layout/Tabs';
import useInvoices from '../patients/detail/billing/transactions/Invoice/hooks/useInvoices';
import BusinessRuleIssue from './BusinessRuleIssue';
import { TabComponentParams } from './IntegrationRoutes';
import InvoiceDataDetails from './InvoiceDataDetails';
import InvoiceStatusDisplay from './InvoiceStatusDisplay';

type Props = TabComponentParams;

const Integration837p: React.FC<Props> = ({
  tabs = [],
  me,
  tab,
  featureTitle,
  tabTitleClassName,
  tabContainerClassName,
}) => {
  const [componentState, setComponentState] = useState<STRING_ANY_HASH>({
    checked: false,
    failed: false,
    success: false,
    complete: false,
  }); // eslint-disable-line

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const incomingInvoiceId = urlParams.get('id');
  const invoiceId = incomingInvoiceId ? parseInt(incomingInvoiceId) : -1;
  const incomingPatientId = urlParams.get('pid') ?? '';
  const incomingBillingKey = urlParams.get('bk') ?? '';

  const mock = urlParams.get('mock') === 'true';
  const { createToast } = useContext(ToastContext);
  // const { data: invoice, isFetching } = useInvoice({ invoiceId });
  const {
    data: invoices,
    isFetching,
    refetch,
  } = useInvoices({
    patientId: incomingPatientId,
    billingKey: incomingBillingKey,
  });

  /**
   * The default data structure has all the main keys to get around null values
   * and the crazy keys/object not existing before we point to them with the
   * various input items.
   */
  const startingPayload = CreateEdiPostDefaults;
  if (!mock) delete startingPayload.context.mock;

  if (incomingBillingKey) {
    startingPayload.context.billingKey = incomingBillingKey;
  }

  const [payload, setPayload] = useState(startingPayload);
  //const [errors, setErrors] = useState({} as FormErrors);
  const [errors] = useState({} as FormErrors);
  const [isRestActive, setIsRestActive] = useState(false);
  const [toggleActivateAll, setToggleActivateAll] = useState(false);
  const [toggleNewLineSegments, setToggleNewLineSegments] = useState(true);
  const [restResponse, setRestResponse] = useState<any>('');
  const [resetHappyPath, setResetHappyPath] = useState(false);
  const [toggleJsonDisplay, setToggleJsonDisplay] = useState(false);
  const [mockHidden, setMockHidden] = useState<STRING_BOOLEAN_HASH | null>(
    null,
  );
  const [availableMockValues, setAvailableMockValues] = useState<
    mockDataType[]
  >([]);
  const [visibleMockValues, setVisibleMockValues] = useState<mockDataType[]>(
    [],
  );

  const fieldsetClassName =
    'border border-gray-400 p-4 bg-white rounded-md mb-4 flex flex-col w-full';
  const legendClassName = 'px-1';

  const rehydrate = (payload: CreateEdiPostType) => {
    const clone = ChiroUpJSON.clone(payload) as CreateEdiPostType,
      objs = clone?.context?.mock?.values || {};

    Object.keys(objs)
      .map((key) => objs[key])
      .forEach((item: any) => {
        if (typeof item.value === 'string' && /^[[{]/.test(item.value)) {
          item.value = ChiroUpJSON.parse(item.value);
        }
      });

    return clone;
  };

  const ediString = useMemo(() => {
    console.log({ ediString: { restResponse } });
    if (!restResponse) return null;
    if (typeof restResponse === 'string') {
      const str = ChiroUpJSON.parse(restResponse).data;
      if (!toggleNewLineSegments) {
        return str;
      } else {
        const pcs = typeof str === 'string' ? str.split('~') : [];
        return pcs.join('~\n');
      }
    }
  }, [restResponse, toggleNewLineSegments]);

  const preflightTest = useMemo(() => {
    if (!invoices) return null;
    const invoice = invoices.find((item: Invoice) => item.id === invoiceId);

    let nobj = {} as Partial<Invoice>;
    let targetStatus = InvoiceStatusEnum.Waiting;
    if (invoice) {
      nobj = ChiroUpJSON.clone(invoice) as Invoice;
      delete nobj.statusData; // This can be big and it is not required to make the judgement.
      delete nobj.invoiceData; // Ibid.
      const nextStatus = AllInvoiceStatusOptions.findIndex((item) => {
        return item.text === nobj.status;
      });
      if (
        nextStatus !== -1 &&
        nextStatus !== AllInvoiceStatusOptions.length - 1
      ) {
        targetStatus = AllInvoiceStatusOptions[nextStatus + 1].text;
      }
    }

    setComponentState((p) => ({
      ...p,
      complete: false,
    }));

    return {
      trace: true,
      rule: 'invoice.0001' as keyof typeof BusinessRuleList,
      context: { invoice: nobj, targetStatus },
    };
  }, [invoices, invoiceId]);

  const invoice = useMemo(() => {
    return (preflightTest?.context?.invoice as InvoiceDetailType) ?? null;
  }, [preflightTest?.context?.invoice]);

  const completeInvoice = useMemo(() => {
    if (!invoices) return null;
    const invoice = invoices.find((item: Invoice) => item.id === invoiceId);
    if (!invoice) return null;
    return ChiroUpJSON.clone(invoice) as Invoice;
  }, [invoices, invoiceId]);

  const HANDLE = {
    preflightTest: async (e: any) => {
      if (e && e.preventDefault) e.preventDefault();
      setIsRestActive(true);
      setComponentState((p) => ({
        ...p,
        count: 0,
        issues: [],
      }));

      try {
        const areWeUpdating =
          componentState?.checked &&
          componentState?.count === 0 &&
          componentState?.issues.length === 0;
        let complete = false;
        if (
          areWeUpdating &&
          preflightTest?.context?.targetStatus === InvoiceStatusEnum.Submitted
        ) {
          const payloadToSend = ChiroUpJSON.clone(rehydrate(payload));
          payloadToSend.context.invoice = invoice;
          payloadToSend.context.targetStatus =
            preflightTest?.context?.targetStatus;
          payloadToSend.context.trace = true;
          delete payloadToSend?.context?.mock;
          const result = await patientBillingService.get837P({
            clinicId: 1,
            payload: payloadToSend,
          });
          if (result) {
            complete = areWeUpdating && result.count === 0;
            setComponentState((p) => ({
              ...p,
              ...result,
              checked: true,
              failed: result.count !== 0,
              success: result.count === 0,
              complete,
            }));
            if (!toggleNewLineSegments) {
              setRestResponse(
                typeof result === 'string'
                  ? result
                  : ChiroUpJSON.pretty(result),
              );
            } else {
              const pcs =
                typeof result === 'string'
                  ? result.split('~')
                  : ChiroUpJSON.pretty(result);
              setRestResponse(Array.isArray(pcs) ? pcs.join('~\n') : pcs);
            }
          }
        } else {
          const result = await businessRulesService.apply({
            clinicId: me?.selectedClinic?.ID ?? -1,
            body: {
              ...preflightTest,
              update: areWeUpdating,
            },
            trace: true,
          });
          console.log({ result });
          if (result) {
            complete = areWeUpdating && result.count === 0;
            const continuing = complete && areWeUpdating;
            setComponentState((p) => ({
              ...p,
              ...result,
              checked: continuing ? false : true,
              failed: result.count !== 0,
              success: result.count === 0,
              complete,
            }));
          }
        }

        if (complete) {
          await refetch();
        }
      } catch (e: unknown) {
        console.log({ e });
      }

      setIsRestActive(false);
    },
    submit: async ({
      e,
      specificPayload,
    }: {
      e: React.MouseEvent<HTMLButtonElement>;
      specificPayload?: CreateEdiPostType;
    }) => {
      setIsRestActive(true);
      if (e.preventDefault) e.preventDefault();
      if (!specificPayload) specificPayload = payload;

      const clone = rehydrate(specificPayload);
      console.log({ submit: { e, clone } });
      try {
        const resp = await patientBillingService.get837P({
          clinicId: 1,
          payload: clone,
        });
        if (!toggleNewLineSegments) {
          setRestResponse(
            typeof resp === 'string' ? resp : ChiroUpJSON.pretty(resp),
          );
        } else {
          const pcs =
            typeof resp === 'string'
              ? resp.split('~')
              : ChiroUpJSON.pretty(resp);
          setRestResponse(Array.isArray(pcs) ? pcs.join('~\n') : pcs);
        }
      } catch (err: any) {
        console.error({ err });
        setRestResponse(
          (err && err.response && err.response.data) || 'REST error.',
        );
      }

      setIsRestActive(false);
    },
    inputChange: (path: string) => (e: any) => {
      if (e.preventDefault) e.preventDefault();

      const clone = setPropertyValueByPath({
        root: payload,
        path,
        value: e,
        clone: true,
      });

      console.log({ inputChange: { e, path }, payload, clone });
      setPayload(clone);
    },
    toggleActivateAll: () => (e: any) => {
      if (e.preventDefault) e.preventDefault();

      const setTo = !toggleActivateAll,
        clone = ChiroUpJSON.clone(payload),
        objs = clone?.context?.mock?.values || {};

      Object.keys(objs)
        .map((key) => objs[key])
        .forEach((item: any) => {
          item.active = setTo;
        });

      setToggleActivateAll(!toggleActivateAll);
      setPayload(clone);
    },
    resetHappyPath: () => (e: any) => {
      if (e.preventDefault) e.preventDefault();
      setResetHappyPath(true);
      setPayload(CreateEdiPostDefaults);
      setTimeout(() => {
        setResetHappyPath(false);
      }, 1500);
    },
    toggleJsonDisplay: () => (e: any) => {
      if (e.preventDefault) e.preventDefault();
      setToggleJsonDisplay(!toggleJsonDisplay);
    },
    hideMockToggle: (item: mockDataType) => (e: any) => {
      if (e.preventDefault) e.preventDefault();
      const newObj = ChiroUpJSON.clone(mockHidden);
      newObj[item.label] = !newObj[item.label];

      setVisibleMockValues(
        availableMockValues.filter((item) => !newObj?.[item.label]),
      );

      setMockHidden(newObj);
      console.log({ hideMockToggle: item });
    },
    toggleNewLineSegments: () => (e: any) => {
      if (e.preventDefault) e.preventDefault();
      setToggleNewLineSegments(!toggleNewLineSegments);
    },
  };

  useEffect(() => {
    /**
     * Probably best to have them alphabetically by the label.
     */
    const newMockData = MOCK_DATA({
      payload,
      handle: HANDLE,
    }).sort((a: any, b: any) => {
      return a.label > b.label ? 1 : b.label > a.label ? -1 : 0;
    });

    let newObj = mockHidden;
    const mockHiddenKeys = newObj ? Object.keys(newObj) : [];

    if (!newObj || mockHiddenKeys.length === 0) {
      newObj = newMockData.reduce((acc: STRING_BOOLEAN_HASH, item) => {
        acc[item.label] = true;
        return acc;
      }, {} as STRING_BOOLEAN_HASH);

      setMockHidden(newObj);
    }

    setAvailableMockValues(newMockData);
    setVisibleMockValues(newMockData.filter((item) => !newObj?.[item.label]));
    console.log({ visibleMockValues, newObj, newMockData });
  }, [payload]);

  const hashDisplay = useMemo(() => {
    if (!invoice) return null;
    return {
      'Legal Name': invoice.legalName,
      'Control Number': invoice.controlNumber,
      Priority: PayorPriorityIdText[Number.parseInt(invoice?.priority || '-1')],
    };
  }, [invoice]);

  if (invoices === null && !isFetching) {
    return (
      <div className="pb-4">
        {tabs && <Tabs tabs={tabs} />}
        <Header title={featureTitle}>
          <div className={tabTitleClassName}>{tab.name}</div>
        </Header>
        <div className="pt-8 w-full h-full flex text-gray-400 justify-center p-4">
          <cite>Unsupported.</cite>
        </div>
      </div>
    );
  }

  if (isFetching && !componentState?.complete) {
    return (
      <div className="pt-8 w-full h-full flex bg-gray-100 justify-center p-4">
        <Loading flag={isFetching} />
      </div>
    );
  }

  if (!mock) {
    return (
      <div className="pb-4">
        {tabs && <Tabs tabs={tabs} />}
        <Header title={featureTitle}>
          <div className={tabTitleClassName}>{tab.name}</div>
        </Header>
        <HashDisplay hash={hashDisplay} header="Invoice" />
        <div className="flex justify-center">
          <InvoiceStatusDisplay
            status={invoice?.status}
            onClick={(e) => {
              if (e && e.preventDefault) e.preventDefault();
              HANDLE.preflightTest(e);
            }}
            transitionStatus={
              componentState?.checked === false
                ? null
                : componentState?.success
                  ? true
                  : false
            }
            isLoading={isRestActive && !componentState?.complete}
            complete={componentState?.complete}
          />
        </div>

        {invoice?.status === InvoiceStatusEnum.Unsubmitted ? (
          <div className="mt-4 mx-8">
            <div className="flex flex-row space-x-4 mb-2 w-full">
              <div className="w-full">
                {' '}
                <fieldset className={fieldsetClassName}>
                  <legend className={legendClassName}>Context</legend>
                  {/* <Select
                    name="context.generation"
                    tooltip={[
                      'Used to specify if this is a first-time claim,',
                      'a replacment or if the previous is being voided.',
                    ].join(' ')}
                    tooltipClassName="w-56"
                    label="Generation"
                    className="-mt-3"
                    options={InvoiceGenerationOptions}
                    value={payload?.context?.generation}
                    onChange={HANDLE.inputChange('context.generation')}
                    limit={1}
                    native
                  />

                  <Select
                    name="context.delay"
                    tooltip="When making a claim is delayed more than 90 days, a reason is required."
                    tooltipClassName="w-56"
                    label="Delay Reason"
                    className=""
                    options={[
                      { text: '-- select delay reason --', value: '' },
                    ].concat(ClaimDelayReasonOptions)}
                    value={payload?.context?.delay}
                    onChange={HANDLE.inputChange('context.delay')}
                    errors={errors?.fieldErrors?.['context.delay']}
                    limit={1}
                    native
                  /> */}

                  <div className="flex mt-4">
                    <Checkbox
                      name={'payload?.context?.update'}
                      value={payload?.context?.update}
                      onChange={HANDLE.inputChange('context.update')}
                      label={`Update Invoice Status`}
                      tooltip={[
                        `When checked, the invoice status to the target status if there are no errors.`,
                        `Use this to preview the 837P before creating a final one if you're testing.`,
                      ].join(' ')}
                    />
                  </div>
                  <div className="text-xl">&nbsp;</div>
                </fieldset>
              </div>
              <div className="w-full">
                <fieldset className={fieldsetClassName}>
                  <legend className={legendClassName}>Receiver</legend>
                  <cite>Receiver Deprecated</cite>
                  {/* 
                  <Input
                    label="Authorization Info"
                    tooltip={[
                      "This looks to be a username, but we'll see.",
                    ].join(' ')}
                    name="receiver.authorization.info"
                    onChange={HANDLE.inputChange('receiver.authorization.info')}
                    value={payload.receiver?.authorization?.info}
                    className="-mt-3"
                  />
                  <Input
                    label="Security Type"
                    tooltip={[
                      'The supported types are unknown at the current time,',
                      'the one in the default data is pretty sure.',
                    ].join(' ')}
                    name="receiver.security.type"
                    onChange={HANDLE.inputChange('receiver.security.type')}
                    value={payload.receiver?.security?.type}
                  />
                  <Input
                    label="Security Value"
                    tooltip={[
                      'If the security type is the default, then this is pretty straight forward.',
                    ].join(' ')}
                    name="receiver.security.value"
                    onChange={HANDLE.inputChange('receiver.security.value')}
                    value={payload.receiver?.security?.value}
                  /> */}
                </fieldset>
              </div>
              <div className="w-full">
                <fieldset className={fieldsetClassName}>
                  <legend className={legendClassName}>Sender</legend>
                  <cite>Sender Deprecated</cite>
                  {/* <div className="-mt-3">
                    <Input
                      label="Sender Site ID"
                      tooltip={[
                        'This should be provided by the EDI vendor.',
                      ].join(' ')}
                      name="sender.siteId"
                      onChange={HANDLE.inputChange('sender.siteId')}
                      value={payload.sender?.siteId}
                    />
                  </div>

                  <div className="flex mt-4">
                    <Checkbox
                      name={'sender.ackRequested'}
                      value={payload?.sender?.ackRequested}
                      onChange={HANDLE.inputChange('sender.ackRequested')}
                      label={`Acknowledgement Requested`}
                      tooltip={`Request the receiving EDI parter send an acknowledgement. [I think.]`}
                    />
                  </div>

                  <div className="text-4xl">&nbsp;</div>
                  <div className="text-3xl">&nbsp;</div> */}
                </fieldset>
              </div>
            </div>
          </div>
        ) : null}

        {componentState?.issues && componentState.count !== 0 ? (
          <div className="px-4 mb-8">
            {componentState.issues.map(
              (issue: BusinessRuleIssueType, idx: number) => (
                <BusinessRuleIssue key={idx} issue={issue} />
              ),
            )}
          </div>
        ) : null}

        <InvoiceDataDetails invoice={completeInvoice} />
        {/* {ediString ? (
          <div className="mt-4 mx-8 relative">
            <fieldset
              className={classNames(fieldsetClassName, 'mt-4 relative')}
            >
              <legend className={legendClassName}>837P</legend>
              <div className="absolute top-0 right-0 flex flex-row">
                <div className="flex mt-1 z-50">
                  <Checkbox
                    name={'toggleNewLineSegments'}
                    value={toggleNewLineSegments}
                    onChange={HANDLE.toggleNewLineSegments()}
                    label={`Split Response Segments`}
                    tooltip={[
                      `When copied or downloaded, the segments ${
                        toggleNewLineSegments ? 'will' : 'will not'
                      } have newlines between them.`,
                      'Newlines makes it easier for humans to read, but the payload',
                      'is more compact without them. Whether these matter during',
                      'an EDI transmission depends on the receiving party.',
                    ].join(' ')}
                  />
                </div>
                <div className="flex">
                  <TrivialDownload
                    text={ediString}
                    containerClassName="mr-7 cursor-pointer"
                  />
                </div>
                <div className="flex mr-4">
                  <CopyToClipboard
                    text={ediString}
                    onCopy={() => {
                      createToast({
                        title: 'Success!',
                        description: <>Text copied to clipboard!</>,
                        type: ToastTypes.Success,
                        duration: 5000,
                      });
                    }}
                    options={{ format: 'text/plain' }}
                  >
                    <ClipboardDocumentIcon
                      className="h-6 w-6 cursor-pointer print:hidden hover:text-primary-600"
                      title="Copy as plain text"
                    />
                  </CopyToClipboard>
                </div>
              </div>
              <Textarea
                label=" "
                name="ediString"
                value={ediString}
                rows={24}
                className="mt-4 w-full font-mono text-xs"
                disabled={true}
                onChange={function (val: string): void {
                  throw new Error('Function not implemented.');
                }}
              />
            </fieldset>
          </div>
        ) : null} */}
        {/* <hr /> */}
        {/* <pre>{ChiroUpJSON.pretty({ invoice, completeInvoice })}</pre> */}
        {/* <pre>
          {ChiroUpJSON.pretty({ preflightTest, componentState, payload })}
        </pre> */}
        {/* <pre>{ChiroUpJSON.pretty(componentState)}</pre>
        // <pre>{ChiroUpJSON.pretty(payload)}</pre> */}
        <div className="mt-8">
          <pre>TODO: Give a message if the invoice status is not good.</pre>
        </div>
      </div>
    );
  }

  return (
    <div>
      {tabs && <Tabs tabs={tabs} />}
      <Header title={featureTitle}>
        <div className={tabTitleClassName}>{tab.name}</div>
      </Header>
      <div className={tabContainerClassName}>
        <form className="w-full">
          <div className="flex flex-row space-x-4 mb-2 w-full">
            <div className="w-full">
              <fieldset className={fieldsetClassName}>
                <legend className={legendClassName}>REST Switches</legend>
                <div className="flex">
                  <Checkbox
                    name={'context.debug'}
                    value={payload?.context?.debug}
                    onChange={HANDLE.inputChange('context.debug')}
                    label="Enable EDI Debug Messages"
                    tooltip={[
                      'This introduces "[page] :: [section] :: [field]" into the EDI output.',
                      'Should be helpful debugging issues.',
                    ].join(' ')}
                  />
                </div>

                <div className="flex mt-4">
                  <Checkbox
                    name={'context.trace'}
                    value={payload?.context?.trace}
                    onChange={HANDLE.inputChange('context.trace')}
                    label="Enable EDI Backend Trace Messages"
                    tooltip={[
                      'The EDI creation is silent in the server logs except for errors',
                      'if this is false. Otherwise, a great amount of data -- maybe',
                      'PHI -- will be in the logs. Probably should never be done in',
                      'production!',
                    ].join(' ')}
                  />
                </div>

                <div className="flex mt-4">
                  <Checkbox
                    name={'context.mock.enable'}
                    value={payload?.context?.mock?.enable}
                    onChange={HANDLE.inputChange('context.mock.enable')}
                    label="Enable Mock Data"
                    tooltip={[
                      'Unchecking this turns off all mock-data processing.',
                    ].join(' ')}
                  />
                </div>

                <div className="flex mt-4">
                  <Checkbox
                    name={'toggleNewLineSegments'}
                    value={toggleNewLineSegments}
                    onChange={HANDLE.toggleNewLineSegments()}
                    label={`Split Response Segments`}
                    tooltip={[
                      `The REST response ${
                        toggleNewLineSegments ? 'will' : 'will not'
                      } have newlines between segments.`,
                      'Newlines makes it easier for humans to read, but the payload',
                      'is more compact (all one line) without them and are not strictly',
                      "required [I don't think].",
                    ].join(' ')}
                  />
                </div>
              </fieldset>
            </div>
            <div className="w-full">
              <fieldset className={fieldsetClassName}>
                <legend className={legendClassName}>UI Control</legend>
                <div className="flex">
                  <Checkbox
                    name={'toggleActivateAll'}
                    value={toggleActivateAll}
                    onChange={HANDLE.toggleActivateAll()}
                    label={`${
                      toggleActivateAll ? 'Deactivate' : 'Activate'
                    } all mock values.`}
                    tooltip={[
                      `This ${
                        toggleActivateAll ? 'deactivate' : 'activate'
                      } all individual mock values.`,
                    ].join(' ')}
                  />
                </div>

                <div className="flex mt-4">
                  <Checkbox
                    name={'resetHappyPath'}
                    value={resetHappyPath}
                    onChange={HANDLE.resetHappyPath()}
                    label="Reset Payload to Happy Path"
                    tooltip={[
                      `Drops in some initial data that works -- for the most part -- in dev.`,
                      'The checkbox will turn off after the data is reset and a second or two delay.',
                    ].join(' ')}
                  />
                </div>
                <div className="flex mt-4">
                  <Checkbox
                    name={'toggleJsonDisplay'}
                    value={toggleJsonDisplay}
                    onChange={HANDLE.toggleJsonDisplay()}
                    label={`${
                      toggleJsonDisplay ? 'Hide' : 'Show'
                    } JSON Output of Raw Payload`}
                    tooltip={[
                      `This ${
                        toggleActivateAll ? 'hides' : 'shows'
                      } the JSON payload in the UI.`,
                    ].join(' ')}
                  />
                </div>
                {/* The div is not necessary, but fills out the empty space. */}
                <div className="text-2xl">&nbsp;</div>
              </fieldset>
            </div>
          </div>

          <fieldset className={fieldsetClassName}>
            <legend className={legendClassName}>Context</legend>
            <div className="flex flex-row space-x-4">
              <div className="w-1/2">
                <Input
                  label="Billing Key"
                  tooltip="This is the appointment or encounter id or the transaction id if there is no appointment or encounter associated with the transaction."
                  name="context.billingKey"
                  onChange={HANDLE.inputChange('context.billingKey')}
                  value={payload.context.billingKey}
                  errors={errors.fieldErrors?.['context.billingKey']}
                  placeholder={`ex. ${CreateEdiPostDefaults.context.billingKey}`}
                  className="-mt-3"
                />
                <Select
                  name="context.payorPriorityCode"
                  tooltip={[
                    'Specify the payor priority to use for the EDI. Note that',
                    'if the next payor does not have the same priority, an',
                    'error will be thrown.',
                  ].join(' ')}
                  tooltipClassName="w-56"
                  label="Payor Priority"
                  className="col-span-12 sm:col-span-6"
                  options={PayorPriorityOptions}
                  value={payload?.context?.payorPriorityCode}
                  onChange={HANDLE.inputChange('context.payorPriorityCode')}
                  limit={1}
                  native
                />
                {/* <Select
                  name="context.generation"
                  tooltip={[
                    'Used to specify if this is a first-time claim,',
                    'a replacment or if the previous is being voided. [And no, have no idea',
                    'how this is interpreted on the other end.]',
                  ].join(' ')}
                  tooltipClassName="w-56"
                  label="Generation"
                  className="col-span-12 sm:col-span-6"
                  options={InvoiceGenerationOptions}
                  value={payload?.context?.generation}
                  onChange={HANDLE.inputChange('context.generation')}
                  limit={1}
                  native
                /> */}
              </div>
              <div className="w-1/2">
                {/* <Select
                  name="context.delay"
                  tooltip="When making a claim is delayed more than 90 days, a reason is required. See mock-data further on to test this."
                  tooltipClassName="w-56"
                  label="Delay Reason"
                  className="-mt-3 col-span-12 sm:col-span-6"
                  options={[
                    { text: '-- select delay reason --', value: '' },
                  ].concat(ClaimDelayReasonOptions)}
                  value={payload?.context?.delay}
                  onChange={HANDLE.inputChange('context.delay')}
                  errors={errors?.fieldErrors?.['context.delay']}
                  limit={1}
                  native
                /> */}
                <Select
                  name="context.targetStatus"
                  tooltip={[
                    'The current (or new) invoice status. If no invoice exists, one is created',
                    'with a pending status. If this has a value, the invoice',
                    'status is set after the EDI is successfully created.',
                  ].join(' ')}
                  tooltipClassName="w-56"
                  label="Invoice Status"
                  className="col-span-12 sm:col-span-6"
                  options={[
                    { text: '-- select invoice status --', value: '' },
                  ].concat(InvoiceStatusOptions)}
                  value={payload?.context?.targetStatus}
                  onChange={HANDLE.inputChange('context.targetStatus')}
                  limit={1}
                  native
                />
                <div className="mt-7">
                  <Checkbox
                    name={'payload?.context?.update'}
                    value={payload?.context?.update}
                    onChange={HANDLE.inputChange('context.update')}
                    label={`Update Invoice Status`}
                    tooltip={[
                      `When checked, the invoice status to the target status if there are no errors.`,
                      `Use this to preview the 837P before creating a final one if you're testing.`,
                    ].join(' ')}
                  />
                </div>
              </div>
            </div>
          </fieldset>

          <div className="flex flex-row space-x-4 mb-2 w-full">
            <div className="w-full">
              <fieldset className={fieldsetClassName}>
                <legend className={legendClassName}>Receiver</legend>
                <cite>Receiver Deprecated</cite>
                {/* <Input
                  label="Authorization Info"
                  tooltip={["This looks to be a username, but we'll see."].join(
                    ' ',
                  )}
                  name="receiver.authorization.info"
                  onChange={HANDLE.inputChange('receiver.authorization.info')}
                  value={payload.receiver?.authorization?.info}
                  className="-mt-3"
                />
                <Input
                  label="Security Type"
                  tooltip={[
                    'The supported types are unknown at the current time,',
                    'the one in the default data is pretty sure.',
                  ].join(' ')}
                  name="receiver.security.type"
                  onChange={HANDLE.inputChange('receiver.security.type')}
                  value={payload.receiver?.security?.type}
                />
                <Input
                  label="Security Value"
                  tooltip={[
                    'If the security type is the default, then this is pretty straight forward.',
                  ].join(' ')}
                  name="receiver.security.value"
                  onChange={HANDLE.inputChange('receiver.security.value')}
                  value={payload.receiver?.security?.value}
                /> */}
              </fieldset>
            </div>
            <div className="w-full">
              <fieldset className={fieldsetClassName}>
                <legend className={legendClassName}>Sender</legend>
                <cite>Sender Deprecated</cite>
                {/* 
                <div className="-mt-3">
                  <Input
                    label="Sender Site ID"
                    tooltip={[
                      'This should be provided by the EDI vendor.',
                    ].join(' ')}
                    name="sender.siteId"
                    onChange={HANDLE.inputChange('sender.siteId')}
                    value={payload.sender?.siteId}
                  />
                </div>

                <div className="flex mt-4">
                  <Checkbox
                    name={'sender.ackRequested'}
                    value={payload?.sender?.ackRequested}
                    onChange={HANDLE.inputChange('sender.ackRequested')}
                    label={`Acknowledgement Requested`}
                    tooltip={`Request the receiving EDI parter send an acknowledgement. [I think.]`}
                  />
                </div>

                <div className="text-4xl">&nbsp;</div>
                <div className="text-3xl">&nbsp;</div> */}
              </fieldset>
            </div>
          </div>
          <fieldset className={fieldsetClassName}>
            <legend className={legendClassName}>Mock Data</legend>
            <div className="grid grid-cols-5 space-x-4">
              <div className="">
                {availableMockValues.map((item: any, idx) => {
                  return (
                    <div
                      key={idx}
                      className={`${
                        !mockHidden?.[item.label]
                          ? 'bg-primary-600 text-white'
                          : ''
                      } cursor-pointer text-sm m-2 p-2 border border-gray-400 bg-gray-50 hover:bg-gray-500 hover:text-white  rounded-md space-x-4 inline-block`}
                      onClick={HANDLE.hideMockToggle(item)}
                    >
                      {item.label}
                      <TrivialTooltip
                        text={item.tooltip}
                        iconClassName={`${
                          !mockHidden?.[item.label] ? 'text-white ' : ''
                        }h-3 w-3 text-gray-400 inline-block ml-2 align-text-top`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="col-span-4">
                {visibleMockValues.map((item: any, idx) => {
                  return <MockItemByType key={idx} attributes={item} />;
                })}
              </div>
            </div>
          </fieldset>

          <Button
            type="submit"
            onClick={(e) => {
              HANDLE.submit({ e });
            }}
            text="Send"
            loading={isRestActive}
            disabled={isRestActive}
          />

          {ediString ? (
            <fieldset
              className={classNames(fieldsetClassName, 'mt-4 relative')}
            >
              <legend className={legendClassName}>EDI</legend>
              <div className="flex gap-2 justify-end">
                <div className="inline-block absolute right-14 top-2">
                  <TrivialDownload text={ediString} />
                </div>
                <CopyToClipboard
                  text={ediString}
                  onCopy={() => {
                    createToast({
                      title: 'Success!',
                      description: <>Text copied to clipboard!</>,
                      type: ToastTypes.Success,
                      duration: 5000,
                    });
                  }}
                  options={{ format: 'text/plain' }}
                >
                  <ClipboardDocumentIcon
                    className="h-6 w-6 cursor-pointer print:hidden absolute right-2 top-2 hover:text-primary-600"
                    title="Copy as plain text"
                  />
                </CopyToClipboard>
              </div>
              <Textarea
                label="837P[ish]"
                name="ediString"
                value={ediString}
                rows={24}
                className="w-full font-mono text-xs"
                disabled={true}
                onChange={function (val: string): void {
                  throw new Error('Function not implemented.');
                }}
              />
            </fieldset>
          ) : null}
          {restResponse ? (
            <fieldset
              className={classNames(fieldsetClassName, 'mt-4 relative')}
            >
              <legend className={legendClassName}>REST Response</legend>
              <div className="flex flex-row gap-2">
                <CopyToClipboard
                  text={restResponse}
                  onCopy={() => {
                    createToast({
                      title: 'Success!',
                      description: <>Text copied to clipboard!</>,
                      type: ToastTypes.Success,
                      duration: 5000,
                    });
                  }}
                  options={{ format: 'text/plain' }}
                >
                  <ClipboardDocumentIcon
                    className="h-6 w-6 cursor-pointer print:hidden absolute right-2 top-2 hover:text-primary-600"
                    title="Copy as plain text"
                  />
                </CopyToClipboard>
              </div>
              <Textarea
                label="EDI 837P[ish]"
                name="restResponse"
                value={restResponse}
                rows={24}
                className="w-full font-mono text-xs"
                disabled={true}
                onChange={function (val: string): void {
                  throw new Error('Function not implemented.');
                }}
              />
            </fieldset>
          ) : null}
          {toggleJsonDisplay ? (
            <div className="relative mt-4">
              <CopyToClipboard
                text={ChiroUpJSON.pretty(rehydrate(payload))}
                onCopy={() => {
                  createToast({
                    title: 'Success!',
                    description: <>Text copied to clipboard!</>,
                    type: ToastTypes.Success,
                    duration: 5000,
                  });
                }}
                options={{ format: 'text/plain' }}
              >
                <ClipboardDocumentIcon
                  className="h-6 w-6 cursor-pointer print:hidden absolute right-0 top-0 hover:text-primary-600"
                  title="Copy as plain text"
                />
              </CopyToClipboard>
              <div style={{ fontSize: '0.5rem', lineHeight: '0.75rem' }}>
                <Textarea
                  label="JSON Payload"
                  value={ChiroUpJSON.pretty(payload)}
                  areaClassName="font-mono"
                  rows={24}
                  disabled={true}
                  onChange={() => {}}
                />
              </div>
            </div>
          ) : null}
        </form>
        {/* <pre>
          {ChiroUpJSON.pretty({ pathname, search, incomingBillingKey })}
        </pre> */}
      </div>
    </div>
  );
};

// type MockProps = {
//   handle?: any;
//   inputComponent: React.ReactNode;
//   active: { path: string; value: boolean };
// };

// const MockContainer: React.FC<MockProps> = ({
//   handle,
//   inputComponent,
//   active,
// }) => {
//   return (
//     <div className="flex flex-row space-x-4 mb-2">
//       <div className="flex w-1/4">{inputComponent}</div>
//       <div className="flex pt-7">
//         <Checkbox
//           name={active.path}
//           value={active.value}
//           onChange={handle.inputChange(active.path)}
//           label="Active"
//         />
//       </div>
//     </div>
//   );
// };

const MockItemBlock: React.FC<mockDataType> = ({
  inputComponent,
  activePath,
  activeValue,
  handle,
}) => {
  return (
    <div className="flex flex-row space-x-4 mb-2">
      <div className="flex w-3/4">{inputComponent}</div>
      <div className="flex pt-7">
        <Checkbox
          name={activePath}
          value={activeValue}
          onChange={handle.inputChange(activePath)}
          label="Active"
        />
      </div>
    </div>
  );
};

const MockItemByType: React.FC<{
  attributes: mockDataType;
}> = ({ attributes }) => {
  const {
    name,
    activePath,
    activeValue,
    path,
    value,
    type,
    className = 'w-full',
    label,
    handle,
    placeholder,
    hidden,
    options,
  } = attributes;
  if (hidden) return null;
  let inputComponent = null;
  if (type === 'textarea') {
    inputComponent = (
      <Textarea
        label={label}
        name={name}
        onChange={handle.inputChange(path)}
        value={value as string}
        rows={9}
        className={classNames(className, 'w-full')}
        areaClassName="text-xs font-mono"
      />
    );
  } else if (type === 'input') {
    inputComponent = (
      <Input
        label={label}
        name={name}
        onChange={handle.inputChange(path)}
        value={value as string}
        className={className}
        placeholder={placeholder}
      />
    );
  } else if (type === 'toggle') {
    inputComponent = (
      <Toggle
        label={label}
        tooltipClassName="w-56"
        onChange={handle.inputChange(path)}
        value={value as boolean}
        className="pt-6"
      />
    );
  } else if (type === 'select') {
    /**
     * Currently, only supporting single select.
     */
    inputComponent = (
      <Select
        label={label}
        name={name}
        onChange={handle.inputChange(path)}
        value={value as string}
        className={className}
        options={options}
        limit={1}
        native
      />
    );
  }

  if (inputComponent) {
    return (
      <MockItemBlock
        inputComponent={inputComponent}
        activePath={activePath}
        activeValue={activeValue}
        handle={handle}
        name={name}
        value={value}
        path={path}
        label={label}
        type={type}
        hidden={hidden}
      />
    );
  }
  return (
    <div>
      <cite>Not supported.</cite>
      <pre>{ChiroUpJSON.pretty(attributes)}</pre>
    </div>
  );
};

type subMockDataType = {
  name: string;
  label: string;
  type: string;
  tooltip?: string;
  placeholder?: string;
  options?: SELECT_OPTION_TYPE[];
};

type mockDataType = subMockDataType & {
  inputComponent?: React.ReactNode;
  path: string;
  value: string | boolean;
  className?: string;
  activePath: string;
  activeValue: boolean;
  tooltipClassName?: string;
  handle: any;
  hidden?: boolean;
};

/**
 * Believe it or not, this should simplify putting in mock data. All
 * that should be needed is to add another working object and a push.
 * That is, unless a new type is needed.
 *
 * @param param0
 * @returns
 */
const MOCK_DATA = ({ payload, handle }: { payload: any; handle: any }) => {
  const dataStructure = [];

  let working: subMockDataType = {
    name: 'appointmentPolicies',
    label: 'Appointment Policies Disciplines',
    type: 'textarea',
    tooltip: [
      'The code uses these to know what disciplines to',
      'go get for the policies on the appointment. All',
      'they need is the disciplineID as indicated in the',
      'example data.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    name: 'daysSinceService',
    label: 'Days Since Service',
    type: 'input',
    tooltip: [
      'Transaction age in days since services were rendered. This',
      'simulates an EDI request either way before (negative) or way',
      'after the allowed day. The code will throw an error if too',
      'early or if the delay is 90 days or more and no delay reason',
      'is provided.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    name: 'encounterClinicianId',
    label: 'Encounter Clinician ID',
    type: 'input',
    tooltip: [
      'Transaction age in days since services were rendered. This',
      'simulates an EDI request either way before (negative) or way',
      'after the allowed day. The code will throw an error if too',
      'early or if the delay is 90 days or more and no delay reason',
      'is provided.',
    ].join(' '),
    placeholder: 'ex. 997feee4-6101-4359-9a2c-57a51247b3f8',
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Initial Payor IDs',
    name: 'initialPayorIds',
    type: 'textarea',
    tooltip: [
      'Transactions have payors. This tells the mock code to go fetch',
      'SPECIFIC payors from the Payors table and use those instead of',
      "whatever may exist on the transaction. Sorry, but you'll need",
      'the actual value of the ID column from that table. Check with',
      'a developer to help find good values.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Transaction Involves Spine',
    name: 'involvesSpinalManipulation',
    type: 'toggle',
    tooltip: [
      'For claim purposes, a service code of 98940, 98941, 98942 indicates',
      'the claim involves spinal manipulation. This matters to some of the logic',
      'when creating the claim and other criteria exist.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Patient is Subscriber',
    name: 'isPatientSubscriber',
    type: 'toggle',
    tooltip: [
      'Is the patient the individual with the insurance or is the patient',
      "covered on someone else's insurance? This affects the segments sent in the EDI",
      'and where the claims end up. This forces it to be one way or the',
      'other.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Transaction Patient Amount Paid',
    name: 'patientAmountPaid',
    type: 'input',
    tooltip: [
      'Normally, this is calculated by the patient payments in the transaction.',
      'However, this allows you to set a specific amount to test the segment has the',
      'correct value.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Transaction Service Total',
    name: 'serviceTotal',
    type: 'input',
    tooltip: [
      'This is normally calculated from the services on the transaction. However,',
      'this supports setting a specific value.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Transaction Insurances (Add)',
    name: 'addTransactionInsurances',
    type: 'textarea',
    tooltip: ['Adds additonal insurance items to the transaction.'].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Transaction Insurances (Replace)',
    name: 'transactionInsurances',
    type: 'textarea',
    tooltip: ['This replaces the insurances on the transaction.'].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Transaction Locked',
    name: 'transactionLocked',
    type: 'toggle',
    tooltip: [
      'Normally, the flag on the transaction is checked. An error is',
      'thrown if the transaction is not locked. This allows you to force',
      'a desired value to see the behavior. Could be used to pre-flight',
      'a transaction, for instance.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  working = {
    label: 'Payor Priority',
    name: 'payorPriority',
    type: 'select',
    options: [{ text: '-- select a priority --', value: '' }].concat(
      PayorPriorityOptions,
    ),
    tooltip: [
      'Regardless of the actual priority of the next payor to be',
      'invoiced, use this as the priority.',
    ].join(' '),
  };
  dataStructure.push(mockItemFactory({ working, payload, handle }));

  return dataStructure;
};

const mockItemFactory = ({
  working,
  payload,
  handle,
}: {
  working: any;
  payload: any;
  handle: any;
}) => {
  const $ptr = payload?.context?.mock?.values?.[working.name];
  if (!$ptr) {
    payload = setPropertyValueByPath({
      root: payload,
      path: `context.mock.values.${working.name}`,
      value: {
        value: '',
        active: false,
      },
      // clone: true,
    });
  }

  return {
    ...working,
    handle,
    value: payload?.context?.mock?.values?.[working.name]?.value,
    path: `context.mock.values.${working.name}.value`,
    activeValue: payload?.context?.mock?.values?.[working.name]?.active,
    activePath: `context.mock.values.${working.name}.active`,
  };
};

export default Integration837p;
